import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, XCircle, Clock, User, Calendar } from 'lucide-react';
import { trackDeepLinkUsage } from '@/utils/deepLinkUtils';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const EntryApproval = () => {
  const { entryId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('timeline');
  const queryClient = useQueryClient();
  
  const [approvalNotes, setApprovalNotes] = useState('');
  const token = searchParams.get('token');

  // Track deep link usage
  useEffect(() => {
    if (entryId) {
      trackDeepLinkUsage('entry_approval', 'view', { entryId, hasToken: !!token });
    }
  }, [entryId, token]);

  // Fetch entry data
  const { data: entry, isLoading, error } = useQuery({
    queryKey: ['timeline-entry-approval', entryId],
    queryFn: async () => {
      if (!entryId) return null;
      
      const { data, error } = await supabase
        .from('profile_timeline_entries')
        .select(`
          *,
          profile_timeline_sections!inner(
            name,
            name_en,
            profile_timeline!inner(
              profiles!inner(username, first_name, last_name)
            )
          ),
          creator:profiles!profile_timeline_entries_creator_id_fkey(
            username, first_name, last_name
          )
        `)
        .eq('id', entryId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!entryId
  });

  // Approve entry mutation
  const approveMutation = useMutation({
    mutationFn: async (approved: boolean) => {
      if (!entryId || !user?.id) throw new Error('Missing required data');
      
      const { error } = await supabase
        .from('profile_timeline_entries')
        .update({
          is_approved: approved,
          approval_notes: approvalNotes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', entryId)
        .eq('user_id', user.id); // Ensure only timeline owner can approve
      
      if (error) throw error;
      
      return approved;
    },
    onSuccess: (approved) => {
      const action = approved ? 'approved' : 'rejected';
      toast.success(`Entry ${action} successfully!`);
      
      trackDeepLinkUsage('entry_approval', action, { 
        entryId, 
        hasNotes: !!approvalNotes 
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['timeline-entry-approval', entryId] });
      queryClient.invalidateQueries({ queryKey: ['timeline-entries'] });
      
      // Navigate back to timeline
      if (entry) {
        const profile = entry.profile_timeline_sections.profile_timeline.profiles;
        navigate(`/timeline/${profile.username}`);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update entry approval');
    }
  });

  const handleApprove = () => {
    approveMutation.mutate(true);
  };

  const handleReject = () => {
    approveMutation.mutate(false);
  };

  if (!user) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">Please log in to approve entries</div>
        </div>
      </AppShell>
    );
  }

  if (isLoading) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">Loading entry...</div>
        </div>
      </AppShell>
    );
  }

  if (error || !entry) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">Entry not found or access denied</div>
        </div>
      </AppShell>
    );
  }

  // Check if user is the timeline owner
  const profile = entry.profile_timeline_sections.profile_timeline.profiles;
  const isOwner = entry.user_id === user.id;

  if (!isOwner) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">You are not authorized to approve this entry</div>
        </div>
      </AppShell>
    );
  }

  // Check if entry is already processed
  if (entry.is_approved !== null) {
    const status = entry.is_approved ? 'approved' : 'rejected';
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-md">
            <div className={`mb-6 w-16 h-16 rounded-full flex items-center justify-center mx-auto ${
              entry.is_approved ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {entry.is_approved ? (
                <CheckCircle className="h-8 w-8 text-green-600" />
              ) : (
                <XCircle className="h-8 w-8 text-red-600" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Entry Already {status.charAt(0).toUpperCase() + status.slice(1)}
            </h1>
            <p className="text-gray-600 mb-6">
              This entry has already been {status}
            </p>
            <Button onClick={() => navigate(`/timeline/${profile.username}`)}>
              View Timeline
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell 
      showHeader={true} 
      showNavigation={false}
      backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
    >
      <div className="container mx-auto py-8 px-4 max-w-2xl">
        <div className="text-center mb-8">
          <div className="mb-4 w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto">
            <Clock className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-uplivion-blue mb-2">
            Entry Approval Required
          </h1>
          <p className="text-gray-600">
            Review and approve this timeline entry
          </p>
        </div>

        <Card className="uplivion-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {entry.title}
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {entry.date && (
                <span>{new Date(entry.date).toLocaleDateString()}</span>
              )}
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                Added by {entry.creator?.[0]?.first_name || 'Unknown'} {entry.creator?.[0]?.last_name || 'User'}
              </span>
            </div>
          </CardHeader>
          
          <CardContent>
            {entry.content && (
              <div className="mb-4">
                <p className="text-gray-700 whitespace-pre-wrap">{entry.content}</p>
              </div>
            )}
            
            {entry.photos && entry.photos.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {entry.photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`Memory photo ${index + 1}`}
                    className="rounded-lg object-cover w-full h-48"
                  />
                ))}
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Section:</strong> {entry.profile_timeline_sections.name}
              </p>
              <p className="text-sm text-blue-800">
                <strong>Privacy Level:</strong> {entry.privacy_level.replace('_', ' ')}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="uplivion-card mb-6">
          <CardHeader>
            <CardTitle>Approval Notes (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Add any notes about your approval decision..."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              className="min-h-[100px]"
            />
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button
            onClick={handleApprove}
            disabled={approveMutation.isPending}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {approveMutation.isPending ? 'Approving...' : 'Approve'}
          </Button>
          
          <Button
            onClick={handleReject}
            disabled={approveMutation.isPending}
            variant="destructive"
            className="flex-1"
          >
            <XCircle className="h-4 w-4 mr-2" />
            {approveMutation.isPending ? 'Rejecting...' : 'Reject'}
          </Button>
        </div>
      </div>
    </AppShell>
  );
};

export default EntryApproval;